/** NOAA tide prediction stations (US) — high/low in popup */

export interface TideStation {
  noaaId: string;
  name: string;
  lat: number;
  lng: number;
  note?: string;
}

export const TIDE_STATIONS: TideStation[] = [
  { noaaId: '9414290', name: 'San Francisco, CA', lat: 37.806, lng: -122.465 },
  { noaaId: '8518750', name: 'The Battery, NY', lat: 40.7, lng: -74.015 },
  { noaaId: '9447130', name: 'Seattle, WA', lat: 47.602, lng: -122.339 },
  { noaaId: '9410660', name: 'Los Angeles, CA', lat: 33.72, lng: -118.272 },
  { noaaId: '1612340', name: 'Honolulu, HI', lat: 21.306, lng: -157.867 },
  { noaaId: '8728690', name: 'Miami, FL', lat: 25.731, lng: -80.162 },
  { noaaId: '9440910', name: 'Astoria, OR', lat: 46.207, lng: -123.768 },
  { noaaId: '8570280', name: 'Baltimore, MD', lat: 39.267, lng: -76.579 },
];

export async function fetchTideHighLow(
  noaaId: string
): Promise<{ html: string } | { error: string }> {
  const now = new Date();
  const begin = new Date(now.getTime() - 24 * 60 * 60 * 1000);
  const end = new Date(now.getTime() + 3 * 24 * 60 * 60 * 1000);
  const fmt = (d: Date) =>
    `${d.getFullYear()}${String(d.getMonth() + 1).padStart(2, '0')}${String(d.getDate()).padStart(2, '0')}`;
  const url = `https://api.tidesandcurrents.noaa.gov/api/prod/datagetter?begin_date=${fmt(begin)}&end_date=${fmt(end)}&station=${noaaId}&product=predictions&datum=MLLW&time_zone=lst_ldt&interval=hilo&units=english&format=json`;

  try {
    const res = await fetch(url);
    if (!res.ok) return { error: `HTTP ${res.status}` };
    const data = (await res.json()) as {
      predictions?: { t: string; v: string; type?: string }[];
    };
    const preds = data.predictions ?? [];
    if (preds.length < 2) return { error: 'No predictions returned.' };

    const parseLocal = (s: string) => new Date(s.replace(' ', 'T'));
    const fmtRange = (a: string, b: string) =>
      `${a.slice(5, 16)}–${b.slice(11, 16)}`; // MM-DD HH:MM–HH:MM

    const times = preds
      .map((p) => ({ ...p, dt: parseLocal(p.t) }))
      .sort((a, b) => a.dt.getTime() - b.dt.getTime());
    const nextIdx = Math.max(
      1,
      times.findIndex((p) => p.dt.getTime() > now.getTime())
    );
    const i = nextIdx === -1 ? Math.max(1, times.length - 1) : nextIdx;
    const prev = times[i - 1];
    const next = times[i];

    const rangeLabel = (turn: { type?: string }) =>
      turn.type === 'H' ? 'Rising → High' : turn.type === 'L' ? 'Falling → Low' : 'Tide';

    const rowClass = (turn: { type?: string }) =>
      turn.type === 'H' ? 'tide-row tide-row--high' : turn.type === 'L' ? 'tide-row tide-row--low' : 'tide-row';

    const mkRow = (a: typeof prev, b: typeof next) =>
      `<div class="${rowClass(b)}"><span class="tide-time">${fmtRange(a.t, b.t)}</span><span class="tide-label">${rangeLabel(b)}</span></div>`;

    const upcomingRows: string[] = [];
    upcomingRows.push(mkRow(prev, next));
    for (let k = 0; k < 3; k++) {
      const a = times[i + k];
      const b = times[i + k + 1];
      if (!a || !b) break;
      upcomingRows.push(mkRow(a, b));
    }

    return {
      html: `<div class="tide-popup">
        <div class="tide-popup-rows">${upcomingRows.join('')}</div>
        <a class="tide-popup-link" href="https://tidesandcurrents.noaa.gov/stationhome.html?id=${noaaId}" target="_blank" rel="noopener">NOAA station →</a>
      </div>`,
    };
  } catch {
    return {
      error:
        'Could not load tides (network/CORS). Open NOAA for this station.',
    };
  }
}
